import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import './Input.scss';

const Input = ({
  extraWrapperClass,
  extraInputClass,
  name,
  value = '',
  debounce = false,
  disabled = false,
  timeout = 500,
  onChange: onChangeProps,
  onBlur: onBlurProps,
  placeholder = '',
  size = 'md',
  type = 'text',
  LeftIcon = null,
  RightIcon = null,
  formProps = {},
  tabIndex,
  isError = false,
  maxWidth,
  onClick,
  isReadOnly = false,
  autoComplete,
  onFocus: onFocusProps,
  pattern,
  format,
  min,
  step,
  max,
  renderField,
  inputRef,
  onEnter,
}) => {
  const rootClassName = 'common-input';
  const [localValue, setLocalValue] = useState(value);
  const timerRef = useRef();
  const inlineStyles = { maxWidth };

  const sizesMap = useMemo(
    () => ({
      sm: `${rootClassName}--size-sm`,
      md: `${rootClassName}--size-md`,
      bg: `${rootClassName}--size-bg`,
    }),
    [],
  );

  const mapFieldSizeClass = useMemo(() => sizesMap[size], [sizesMap, size]);

  const handleOnChange = useCallback(
    (event) => {
      const newValue = event.target.value;
      setLocalValue(newValue);

      if (onChangeProps) {
        if (debounce) {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          timerRef.current = window.setTimeout(() => {
            onChangeProps(newValue);
          }, timeout);

          return;
        }

        onChangeProps(newValue);
      }
    },
    [onChangeProps, debounce, timeout],
  );

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
    }
  };

  return (
    <div
      onClick={handleClick}
      style={inlineStyles}
      className={clsx(
        `${rootClassName}_wrapper`,
        mapFieldSizeClass,
        isError && `${rootClassName}_wrapper--error`,
        extraWrapperClass,
      )}
      role='presentation'>
      {LeftIcon ? <div className={clsx(`${rootClassName}_icon`, `${rootClassName}_icon--left`)}>{LeftIcon}</div> : null}

      {renderField ? (
        renderField({
          id: name,
          name,
          value: localValue,
          placeholder,
          type,
          onChange: handleOnChange,
          onBlur: onBlurProps,
          disabled,
          tabIndex,
          readOnly: isReadOnly,
          autoComplete,
          onFocus: onFocusProps,
          pattern,
          format,
          min,
          step,
          max,
          onKeyDown: handleKeyDown,
        })
      ) : (
        <input
          id={name}
          name={name}
          value={localValue}
          placeholder={placeholder}
          type={type}
          onChange={handleOnChange}
          onBlur={onBlurProps}
          disabled={disabled}
          tabIndex={tabIndex}
          readOnly={isReadOnly}
          autoComplete={autoComplete}
          onFocus={onFocusProps}
          pattern={pattern}
          format={format}
          min={min}
          step={step}
          max={max}
          ref={inputRef}
          onKeyDown={handleKeyDown}
          className={clsx(
            `${rootClassName}_field`,
            !!LeftIcon && `${rootClassName}_field--with-icon-left`,
            !!RightIcon && `${rootClassName}_field--with-icon-right`,
            isError && `${rootClassName}_field--error`,
            extraInputClass,
          )}
          {...formProps}
        />
      )}

      {RightIcon ? (
        <div className={clsx(`${rootClassName}_icon`, `${rootClassName}_icon--right`)}>{RightIcon}</div>
      ) : null}
    </div>
  );
};

export default Input;
